import React, { useState, useRef, useEffect } from "react";
import { Drawer, Button, Input, Spin, Space, Typography, Select, Tag, Card, notification, Modal } from "antd";
import { useDSEChat } from "../../hooks/useDSEChat";
import { CloudUploadOutlined, SendOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Text, Title, Link } = Typography;
const { Option } = Select;

const DseChatComponent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [input, setInput] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  
  const [isAzureModalVisible, setIsAzureModalVisible] = useState(false);
  const [azureForm, setAzureForm] = useState({
    description: "",
    suggested_type: "bug",
    urgency: "medium" as "low" | "medium" | "high",
    additional_context: "User submitted via DSE Chat Component",
  });

  const { 
    messages, 
    sendMessage, 
    isLoading, 
    chatState,
    handleStartTicket,
    handleSubmitAnswers,
    handleTicketAction,
    clarifyingQuestions,
    draftPreview,
    draft,
    handleAzureSubmit, 
    isSyncing,
  } = useDSEChat("santanu.barman@bayer.com", "santanu");

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, chatState]);

  useEffect(() => {
    if (draft) {
      setAzureForm((prev) => ({
        ...prev,
        description: draft.description || "",
        suggested_type: draft.ticket_type || "bug",
        urgency: (draft.priority as any) || "medium",
      }));
    } else if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.type === "user") {
        setAzureForm((prev) => ({ ...prev, description: lastMsg.content }));
      }
    }
  }, [draft, messages]);

  /** Only allow http(s) URLs and data:image/* URIs — block javascript:, data:text/html, etc. */
  const isSafeImageSrc = (src: string): boolean =>
    /^https?:\/\//i.test(src) || /^data:image\//i.test(src);

  // --- NEW: Helper to format text with Clickable Links ---
  const formatTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <Link key={i} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  // Helper to render text that may contain <img> tags and/or URLs
  const renderContentWithMedia = (text: string): React.ReactNode[] => {
    const tokenRegex = /(<img\s[^>]*\/?>)|(https?:\/\/[^\s<>"]+)/gi;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      if (match[1]) {
        // <img> tag — render as actual image
        const imgTag = match[1];
        const srcMatch = imgTag.match(/src=["']([^"']+)["']/i);
        const altMatch = imgTag.match(/alt=["']([^"']*)["']/i);
        const src = srcMatch ? srcMatch[1] : "";
        const alt = altMatch ? altMatch[1] : "image";
        if (src && isSafeImageSrc(src)) {
          parts.push(
            <img
              key={`img-${match.index}`}
              src={src}
              alt={alt}
              style={{ maxWidth: "100%", borderRadius: 4, marginTop: 4, marginBottom: 4, display: "block" }}
            />,
          );
        }
      } else if (match[2]) {
        // URL — render as clickable link
        const url = match[2];
        parts.push(
          <Link key={`link-${match.index}`} href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </Link>,
        );
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  // --- NEW: Helper to render Bot Message with Headers (##) ---
  // const renderBotMessage = (content: string) => {
  //   const lines = content.split("\n");
  //   return lines.map((line, index) => {
  //     const trimmedLine = line.trim();

  //     // Handle ## as Questions/Headers
  //     if (trimmedLine.startsWith("##")) {
  //       return (
  //         <Title level={5} key={index} style={{ marginTop: 14, marginBottom: 8, color: "#0078d4" }}>
  //           {trimmedLine.replace(/^##\s*/, "")}
  //         </Title>
  //       );
  //     }

  //     // Handle List Items
  //     if (trimmedLine.startsWith("- ")) {
  //       return (
  //         <div key={index} style={{ paddingLeft: 12, marginBottom: 4, display: "flex" }}>
  //           <span style={{ marginRight: 8 }}>•</span>
  //           <span>{formatTextWithLinks(trimmedLine.substring(2))}</span>
  //         </div>
  //       );
  //     }

  //     // Handle standard text
  //     if (trimmedLine === "") return <div key={index} style={{ height: 8 }} />;
      
  //     return (
  //       <div key={index} style={{ marginBottom: 6 }}>
  //         {formatTextWithLinks(line)}
  //       </div>
  //     );
  //   });
  // };

  const renderBotMessage = (content: string, ticketUrl?: string) => {
    // Split by <img> tags FIRST ([\s\S]*? so it matches across line breaks)
    // This prevents long base64 src values from being broken by the \n split below.
    const imgTagRegex = /<img\s[\s\S]*?\/?\s*>/gi;
    type Segment = { type: "text"; value: string } | { type: "img"; value: string };
    const segments: Segment[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;

    while ((m = imgTagRegex.exec(content)) !== null) {
      if (m.index > lastIndex) {
        segments.push({ type: "text", value: content.slice(lastIndex, m.index) });
      }
      segments.push({ type: "img", value: m[0] });
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < content.length) {
      segments.push({ type: "text", value: content.slice(lastIndex) });
    }

    const result: React.ReactNode[] = [];

    segments.forEach((segment, segIdx) => {
      if (segment.type === "img") {
        const srcMatch = segment.value.match(/src=["']([^"']+)["']/i);
        const altMatch = segment.value.match(/alt=["']([^"']*)["']/i);
        const src = srcMatch ? srcMatch[1] : "";
        const alt = altMatch ? altMatch[1] : "image";
        if (src && isSafeImageSrc(src)) {
          result.push(
            <img
              key={`img-${segIdx}`}
              src={src}
              alt={alt}
              style={{ maxWidth: "100%", borderRadius: 4, marginTop: 8, marginBottom: 8, display: "block" }}
            />,
          );
        }
        return;
      }

      // Text segment — process line by line
      segment.value.split("\n").forEach((line, lineIdx) => {
        const trimmedLine = line.trim();
        const key = `${segIdx}-${lineIdx}`;

        // Handle Headers
        if (trimmedLine.startsWith("##")) {
          result.push(
            <Title level={5} key={key} style={{ marginTop: 14, marginBottom: 8, color: "#0078d4" }}>
              {trimmedLine.replace(/^##\s*/, "")}
            </Title>,
          );
          return;
        }

        // Handle Ticket ID link
        if (trimmedLine.includes("**Ticket ID:**")) {
          const idMatch = trimmedLine.match(/Ticket ID:\*\*\s*(\d+)/);
          const ticketId = idMatch ? idMatch[1] : null;
          result.push(
            <div key={key} style={{ marginBottom: 8 }}>
              <Text strong>Ticket ID: </Text>
              {ticketId && ticketUrl ? (
                <Link href={ticketUrl} target="_blank" rel="noopener noreferrer">
                  {ticketId}
                </Link>
              ) : (
                <Text>{ticketId || "N/A"}</Text>
              )}
            </div>,
          );
          return;
        }

        // Handle List Items
        if (trimmedLine.startsWith("- ")) {
          result.push(
            <div key={key} style={{ paddingLeft: 12, marginBottom: 4, display: "flex" }}>
              <span style={{ marginRight: 8 }}>•</span>
              <span>{renderContentWithMedia(trimmedLine.substring(2))}</span>
            </div>,
          );
          return;
        }

        // Standard paragraph
        result.push(
          <div key={key} style={{ marginBottom: 8 }}>
            {renderContentWithMedia(line)}
          </div>,
        );
      });
    });

    return result;
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    const val = input;
    setInput(""); 
    await sendMessage(val);
  };

  // const onFinalSubmit = async () => {
  //   try {
  //     const action = !draftPreview ? "create_ticket" : "approve";
  //     await handleTicketAction(action as any);
  //     if (action === "create_ticket") {
  //       setAnswers({}); 
  //       notification.success({ 
  //           message: "Ticket Submitted", 
  //           description: "Your support ticket has been created.",
  //           placement: "bottomRight" 
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Submission failed", error);
  //   }
  // };

  const onFinalSubmit = async () => {
    try {
      const action = !draftPreview ? "create_ticket" : "approve";
      const result = await handleTicketAction(action as any) as any;

      // If the backend returns a success message, we show it
      if (result?.success && action === "create_ticket") {
        setAnswers({});
        
        // Success Notification
        notification.success({
          message: "Ticket Created",
          description: `Ticket #${result.ticket_id} is live!`,
          placement: "bottomRight",
        });

        // The hook 'messages' array usually updates automatically if the 
        // backend returns the new message in the chat history.
        // If not, you can manually trigger a message refresh here.
      }
    } catch (error) {
      console.error("Submission failed", error);
    }
  };

  const onCancel = async () => {
    await handleTicketAction("cancel");
    setAnswers({});
  };

  const triggerAzureSync = async () => {
    if (azureForm.description.length < 10) {
      notification.error({ message: "Validation Error", description: "Azure requires at least 10 characters." });
      return;
    }
    const res = await handleAzureSubmit("some-session-id", azureForm);
    if (res?.success) {
      setIsAzureModalVisible(false);
      setAzureForm({ description: "", suggested_type: "bug", urgency: "medium", additional_context: "User submitted via DSE Chat Component" });
      
    }
  };

  return (
    <>
      <Button 
        type="primary" 
        shape="round" 
        onClick={() => setVisible(true)} 
        style={{ 
          position: "fixed", 
          bottom: 24, 
          right: 24, 
          zIndex: 1000, 
          height: 48,
          padding: "0 24px",
          fontWeight: 600,
          fontSize: "15px",
          background: "#0078d4", // Bayer Blue
          border: "2px solid #fff",
          boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <SendOutlined rotate={-45} /> DocsBuddy
      </Button>

      {/* Azure DevOps Modal */}
      <Modal
        title="Create Azure DevOps Ticket"
        open={isAzureModalVisible}
        onOk={triggerAzureSync}
        confirmLoading={isSyncing}
        onCancel={() => setIsAzureModalVisible(false)}
        okText="Submit to Azure"
        okButtonProps={{ style: { backgroundColor: "#0078d4" } }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Text strong>Work Item Type</Text>
            <Select style={{ width: "100%" }} value={azureForm.suggested_type} onChange={(v) => setAzureForm({ ...azureForm, suggested_type: v })}>
              <Option value="bug">Bug</Option>
              <Option value="feature_request">Feature Request</Option>
              <Option value="task">Task</Option>
            </Select>
          </div>
          <div>
            <Text strong>Urgency</Text>
            <Select style={{ width: "100%" }} value={azureForm.urgency} onChange={(v) => setAzureForm({ ...azureForm, urgency: v })}>
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
            </Select>
          </div>
          <div>
            <Text strong>Description (Min 10 chars)</Text>
            <TextArea rows={4} value={azureForm.description} status={azureForm.description.length > 0 && azureForm.description.length < 10 ? "error" : ""} onChange={(e) => setAzureForm({ ...azureForm, description: e.target.value })} placeholder="Describe the issue in detail..." />
          </div>
        </Space>
      </Modal>

      <Drawer 
        title="Agent Chat" 
        placement="right" 
        onClose={() => setVisible(false)} 
        open={visible} 
        width={550}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ flex: 1, overflowY: "auto", marginBottom: 8, paddingRight: 8 }}>
            
            {/* 1. Message History */}
            {messages.map((m) => (
              <div key={m.id} style={{ display: "flex", justifyContent: m.type === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
                <div style={{
                  maxWidth: "90%", padding: "12px 16px", borderRadius: 12,
                  backgroundColor: m.type === "user" ? "#1890ff" : "#ffffff",
                  color: m.type === "user" ? "#fff" : "#000",
                  border: m.type === "user" ? "none" : "1px solid #e8e8e8",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                }}>
                  {/* MODIFIED: Bot messages now use the formatter */}
                  {m.type === "bot" ? renderBotMessage(m.content, m.ticketUrl) : m.content}
                </div>
              </div>
            ))}

            {/* 2. Yes/No Prompt */}
            {chatState === "ticket_prompt" && (
              <div style={{ textAlign: "center", margin: "20px 0", padding: "15px", border: "1px dashed #d9d9d9", borderRadius: "8px" }}>
                <Text strong style={{ display: "block", marginBottom: 10 }}>Would you like to open a support ticket?</Text>
                <Space>
                  <Button type="primary" onClick={() => setIsAzureModalVisible(true)}>Yes, Create Ticket</Button>
                  <Button onClick={onCancel}>No, Cancel</Button>
                </Space>
              </div>
            )}

            {/* 3. Questions Form */}
            {chatState === "ticket_questions" && (
              <div style={{ background: "#fafafa", padding: "15px", borderRadius: "8px", border: "1px solid #d9d9d9", marginBottom: "10px" }}>
                <Text strong style={{ display: "block", marginBottom: 12 }}>Clarifying Questions</Text>
                {clarifyingQuestions.map((q, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: "12px", fontWeight: 500, marginBottom: 4 }}>
                        {q.question} {q.required && <span style={{ color: "red" }}>*</span>}
                    </div>
                    {q.options && q.options.length > 0 ? (
                      <Select placeholder="Select..." style={{ width: "100%" }} onChange={(v) => setAnswers(p => ({ ...p, [q.field_name]: v }))}>
                        {q.options.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
                      </Select>
                    ) : (
                      <Input placeholder="Type here..." onChange={(e) => setAnswers(p => ({ ...p, [q.field_name]: e.target.value }))} />
                    )}
                  </div>
                ))}
                <Space>
                  <Button type="primary" onClick={() => handleSubmitAnswers(answers)} disabled={clarifyingQuestions.some(q => q.required && !answers[q.field_name])}>Submit</Button>
                  <Button onClick={onCancel}>Cancel</Button>
                </Space>
              </div>
            )}

            {/* 4. Ticket Review Card */}
            {chatState === "ticket_review" && (
              <div style={{ background: "#fffbe6", padding: "20px", borderRadius: "8px", border: "1px solid #ffe58f" }}>
                <Title level={5}>{!draftPreview ? "✅ Draft Approved" : "✨ Ready for Review"}</Title>
                {draft && (
                  <Card size="small" style={{ marginTop: 15, marginBottom: 15 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>Type: </Text> <Tag color="blue">{draft.ticket_type?.replace("_", " ").toUpperCase()}</Tag>
                      <Text strong style={{ marginLeft: 10 }}>Priority: </Text> <Tag color={draft.priority === "high" ? "red" : "orange"}>{draft.priority?.toUpperCase()}</Tag>
                    </div>
                    <div style={{ marginBottom: 8 }}><Text strong>Title: </Text> <Text>{draft.title}</Text></div>
                  </Card>
                )}
                <Space style={{ marginTop: 20, width: "100%", justifyContent: "center" }}>
                  <Button type="primary" size="large" onClick={onFinalSubmit}>Create Ticket</Button>
                  <Button size="large" onClick={() => setIsAzureModalVisible(true)} style={{ backgroundColor: "#0078d4", color: "#fff" }}>To Azure</Button>
                  <Button onClick={onCancel} danger>Discard</Button>
                </Space>
              </div>
            )}

            {isLoading && <div style={{ margin: "20px 0", textAlign: "center" }}><Spin size="small" /></div>}
            <div ref={messagesEndRef} />
          </div>

          <div style={{ borderTop: "1px solid #eee", paddingTop: 12 }}>
            <Button icon={<CloudUploadOutlined />} onClick={() => setIsAzureModalVisible(true)} style={{ width: "100%", marginBottom: 8, backgroundColor: "#0078d4", color: "#fff" }}>
              New Azure DevOps Ticket
            </Button>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={3}
              placeholder={chatState === "idle" ? "Ask a question..." : "Follow steps above..."}
              onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleSend(); } }}
              disabled={isLoading || chatState === "ticket_questions" || chatState === "ticket_review"}
            />
            <Button type="primary" onClick={handleSend} loading={isLoading} style={{ marginTop: 8, width: "100%" }} icon={<SendOutlined />}>Send</Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default DseChatComponent;